import React, { useEffect, useState, useCallback } from 'react';

import { Typography, Breadcrumb, Card, Button, Space, message, Row, Col, Tag, Divider, Alert } from 'antd';

import { FacebookOutlined, CheckCircleOutlined, DisconnectOutlined, LoginOutlined } from '@ant-design/icons';

import { useAuth } from '../../../contexts/AuthContext';



const { Title, Paragraph, Text } = Typography;



const FacebookManager = () => {

  const { token, currentUser } = useAuth();

  const [accounts, setAccounts] = useState([]);

  const [maxAccounts, setMaxAccounts] = useState(3);

  const [connecting, setConnecting] = useState(false);

  const [loading, setLoading] = useState(true);

  const [pages, setPages] = useState([]);

  const [loadingPages, setLoadingPages] = useState(false);



  // Connect to Facebook via OAuth

  const handleConnectFacebook = () => {

    setConnecting(true);

    

    // Determine backend URL
    // changed by me: use REACT_APP_BACKEND_URL env var first (supports ngrok for local FB OAuth)
    // original hardcoded logic kept below as reference:
    // const backendUrl = (() => {
    //   const hostname = window.location.hostname;
    //   if (hostname === 'app.pathmarkr.com' || hostname === 'pathmarkr.com' || hostname.includes('amazonaws.com')) {
    //     return 'https://api.pathmarkr.com';
    //   }
    //   if (hostname === 'localhost' || hostname === '127.0.0.1') {
    //     return 'http://localhost:5000';
    //   }
    //   return 'https://api.pathmarkr.com';
    // })();

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.pathmarkr.com'; // changed by me

    

    const fbAppId = process.env.REACT_APP_FACEBOOK_APP_ID || 'YOUR_FACEBOOK_APP_ID';

    const redirectUri = encodeURIComponent(`${backendUrl}/api/integrations/meta/callback`);

    const scope = encodeURIComponent('ads_management,ads_read');

    const state = encodeURIComponent(JSON.stringify({ userId: currentUser?.id, timestamp: Date.now() }));

    

    const fbLoginUrl = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${fbAppId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}&response_type=code`;

    

    // Open Facebook login in a popup or redirect

    window.location.href = fbLoginUrl;

  };



  // Disconnect a specific Facebook account by slot

  const handleDisconnectFacebook = async (accountSlot) => {

    try {

      const res = await fetch(`/api/integrations/facebook/disconnect?slot=${accountSlot}`, {

        method: 'POST',

        headers: {

          'Content-Type': 'application/json',

          ...(token ? { Authorization: `Bearer ${token}` } : {}),

        },

      });

      

      if (!res.ok) throw new Error('Failed to disconnect');

      

      // Remove the disconnected account from state

      setAccounts(prev => prev.filter(acc => acc.account_slot !== accountSlot));

      message.success('Facebook account disconnected successfully');

    } catch (error) {

      message.error('Failed to disconnect Facebook account');

    }

  };



  const loadPages = useCallback(async () => {

    if (!token) return;

    setLoadingPages(true);

    try {

      const res = await fetch('/api/integrations/facebook/pages', {

        headers: { Authorization: `Bearer ${token}` },

      });

      if (res.ok) {

        const data = await res.json();

        setPages(data.pages || []);

      }

    } catch (error) {

      console.error('Error loading Facebook pages:', error);

    } finally {

      setLoadingPages(false);

    }

  }, [token]);



  useEffect(() => {

    const load = async () => {

      try {

        if (!token) {

          setLoading(false);

          return;

        }

        const res = await fetch('/api/integrations/facebook', {

          headers: { Authorization: `Bearer ${token}` },

        });

        if (!res.ok) {

          setLoading(false);

          return;

        }

        const data = await res.json();

        if (!data) {

          setLoading(false);

          return;

        }

        

        // Handle new multi-account response format

        if (data.accounts) {

          setAccounts(data.accounts);

          setMaxAccounts(data.maxAccounts || 3);

        } else if (data.access_token && data.app_id) {

          // Legacy single account format - convert to array

          setAccounts([{

            account_slot: 1,

            account_name: data.account_name || 'Facebook Account',

            account_email: data.account_email || '',

            business_manager_id: data.business_manager_id || '',

            connected_at: data.connected_at || new Date().toISOString(),

            access_token: data.access_token,

          }]);

        }

      } catch (e) {

        // don't block UI on load errors

      } finally {

        setLoading(false);

      }

    };

    load();

    loadPages();

  }, [token, loadPages]);

  

  const canAddMore = accounts.length < maxAccounts;



  return (

    <div style={{ padding: 24 }}>

      <Breadcrumb items={[{ title: 'Client' }, { title: 'Integrations' }, { title: 'Facebook Ads Manager' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>

        <Title level={3} style={{ margin: 0 }}>Facebook Ads Manager</Title>

      </div>



      {loading ? (

        <div style={{ textAlign: 'center', padding: 48 }}>

          <Text type="secondary">Loading accounts...</Text>

        </div>

      ) : (

        <Row gutter={[16, 16]}>

          {/* Connected Accounts */}

          {accounts.length > 0 && (

            <Col span={24}>

              <Alert

                message={`${accounts.length} of ${maxAccounts} Facebook Accounts Connected`}

                description="You can connect up to 3 Facebook accounts to manage ads from multiple business pages."

                type="success"

                icon={<CheckCircleOutlined />}

                showIcon

                style={{ marginBottom: 16 }}

              />

            </Col>

          )}

          

          {/* Display each connected account */}

          {accounts.map((account) => (

            <Col xs={24} md={8} key={account.account_slot}>

              <Card 

                title={

                  <Space>

                    <FacebookOutlined style={{ fontSize: 20, color: '#1877f2' }} />

                    <span>Account {account.account_slot}</span>

                  </Space>

                }

                extra={<Tag color="success" icon={<CheckCircleOutlined />}>Active</Tag>}

                style={{ height: '100%' }}

              >

                <Space direction="vertical" size="small" style={{ width: '100%' }}>

                  <div>

                    <Text type="secondary" style={{ fontSize: 12 }}>Account Name</Text>

                    <div><Text strong>{account.account_name || 'Facebook Account'}</Text></div>

                  </div>

                  <div>

                    <Text type="secondary" style={{ fontSize: 12 }}>Email</Text>

                    <div><Text>{account.account_email || 'Not available'}</Text></div>

                  </div>

                  <div>

                    <Text type="secondary" style={{ fontSize: 12 }}>Business Manager ID</Text>

                    <div><Text code>{account.business_manager_id || 'Not set'}</Text></div>

                  </div>

                  <Divider style={{ margin: '12px 0' }} />

                  <Button 

                    danger 

                    size="small"

                    icon={<DisconnectOutlined />}

                    onClick={() => handleDisconnectFacebook(account.account_slot)}

                    block

                  >

                    Disconnect

                  </Button>

                </Space>

              </Card>

            </Col>

          ))}

          

          {/* Add New Account Card */}

          {canAddMore && (

            <Col xs={24} md={accounts.length > 0 ? 8 : 12}>

              <Card 

                title={

                  <Space>

                    <FacebookOutlined style={{ fontSize: 20, color: '#1877f2' }} />

                    <span>{accounts.length === 0 ? 'Connect Facebook Business Manager' : 'Add Another Account'}</span>

                  </Space>

                }

                style={{ height: '100%', borderStyle: accounts.length > 0 ? 'dashed' : 'solid' }}

              >

                <Space direction="vertical" size="middle" style={{ width: '100%' }}>

                  {accounts.length === 0 && (

                    <>

                      <Paragraph type="secondary">

                        Connect your Facebook account with one click using OAuth. This is the fastest and most secure way to get started.

                      </Paragraph>

                      <ul style={{ paddingLeft: 20, margin: 0, fontSize: 13 }}>

                        <li>Secure OAuth authentication</li>

                        <li>Automatic token management</li>

                        <li>Access to all ad accounts</li>

                        <li>Connect up to {maxAccounts} accounts</li>

                      </ul>

                    </>

                  )}

                  {accounts.length > 0 && (

                    <Paragraph type="secondary" style={{ textAlign: 'center' }}>

                      You can add {maxAccounts - accounts.length} more account{maxAccounts - accounts.length > 1 ? 's' : ''}.

                    </Paragraph>

                  )}

                  <Button 

                    type="primary" 

                    size="large" 

                    icon={<LoginOutlined />}

                    onClick={handleConnectFacebook}

                    loading={connecting}

                    block

                  >

                    {accounts.length === 0 ? 'Connect with Facebook' : 'Add Facebook Account'}

                  </Button>

                </Space>

              </Card>

            </Col>

          )}

          

          {/* Max accounts reached message */}

          {!canAddMore && accounts.length >= maxAccounts && (

            <Col span={24}>

              <Alert

                message="Maximum Accounts Reached"

                description={`You have connected the maximum of ${maxAccounts} Facebook accounts. Disconnect an existing account to add a new one.`}

                type="info"

                showIcon

              />

            </Col>

          )}

        </Row>

      )}

      

      {/* Facebook Pages Section */}

      {accounts.length > 0 && (

        <div style={{ marginTop: 32 }}>

          <Divider />

          <Title level={4} style={{ marginBottom: 16 }}>Facebook Pages</Title>

          <Paragraph type="secondary" style={{ marginBottom: 20 }}>

            These are the Facebook Pages you can manage from your connected accounts.

          </Paragraph>

          

          {loadingPages ? (

            <div style={{ textAlign: 'center', padding: 48 }}>

              <Text type="secondary">Loading pages...</Text>

            </div>

          ) : pages.length === 0 ? (

            <Alert

              message="No Pages Found"

              description="No Facebook Pages were found in your connected accounts. Make sure your Facebook account has admin access to at least one Page."

              type="info"

              showIcon

            />

          ) : (

            <Row gutter={[16, 16]}>

              {pages.map((page) => (

                <Col xs={24} md={8} key={page.id}>

                  <Card

                    size="small"

                    title={

                      <Space>

                        <FacebookOutlined style={{ color: '#1877f2' }} />

                        <span>{page.name}</span>

                      </Space>

                    }

                    extra={<Tag color="blue">Page</Tag>}

                  >

                    <Space direction="vertical" size="small" style={{ width: '100%' }}>

                      <div>

                        <Text type="secondary" style={{ fontSize: 12 }}>Category</Text>

                        <div><Text>{page.category || 'N/A'}</Text></div>

                      </div>

                      <div>

                        <Text type="secondary" style={{ fontSize: 12 }}>Page ID</Text>

                        <div><Text code style={{ fontSize: 11 }}>{page.id}</Text></div>

                      </div>

                      <div>

                        <Text type="secondary" style={{ fontSize: 12 }}>Connected via</Text>

                        <div><Text>{page.account_name || `Account ${page.account_slot}`}</Text></div>

                      </div>

                      {page.instagram_business_account && (

                        <Tag color="purple" style={{ marginTop: 8 }}>Instagram Connected</Tag>

                      )}

                    </Space>

                  </Card>

                </Col>

              ))}

            </Row>

          )}

        </div>

      )}

    </div>

  );

};



export default FacebookManager;

